import { useMemo } from 'react';

import { useSessionStorage } from '../hooks/useSessionStorage';

import { closeAIChat, openAIChat, toggleAIChat } from './aiChatStateStore';
import { CONCIERGE_CHAT_IS_OPEN } from './constants';

export interface ChatState {
  /** Whether the chat is currently open */
  isOpen: boolean;
  /** Function to open the chat */
  open: () => void;
  /** Function to close the chat */
  close: () => void;
  /** Function to toggle the chat open/closed state */
  toggle: () => void;
}

/**
 * Hook to manage AI chat state and provide control methods.
 *
 * This hook provides a consistent interface for controlling the AI chat widget,
 * including opening, closing, and toggling the chat, as well as checking if it's open.
 * The chat is only available on staging2.zuora.com and one.stg.na.zuora.com and persists its open/closed state
 * in session storage.
 *
 * @returns {ChatState} Object containing chat state and control methods:
 *   - `isOpen`: Boolean indicating if the chat is currently open
 *   - `open`: Function to open the chat
 *   - `close`: Function to close the chat
 *   - `toggle`: Function to toggle the chat open/closed state
 *
 * @example
 * ```tsx
 * function ChatButton() {
 *   const { isOpen, open, close, toggle } = useAIChatState();
 *
 *   return (
 *     <button onClick={toggle}>
 *       {isOpen ? 'Close Chat' : 'Open Chat'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useAIChatState(): ChatState {
  const [isOpen] = useSessionStorage(CONCIERGE_CHAT_IS_OPEN, false);

  return useMemo(() => {
    return {
      close: closeAIChat,
      isOpen,
      open: openAIChat,
      toggle: toggleAIChat,
    };
  }, [isOpen]);
}
